import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Plus,
  Upload,
  Search,
  ArrowUpRight,
  Copy,
  Archive,
  ClipboardCheck,
  Clock3,
  LayoutGrid,
  List,
  ChevronRight,
  Layers3,
  Check,
  Loader2,
} from "../components/PixelIcons";
import {
  flatten,
  type DefinitionRecord,
  type Assignment,
} from "@bitsandflames/questionnaire-core";
import { templates } from "../../packages/questionnaire-core/src/templates";
import { useResource, write, date } from "../lib/api";
import {
  Button,
  Badge,
  Modal,
  Field,
  Empty,
  Spinner,
  ErrorBox,
  Select,
  useToast,
} from "../components/ui";
import { useWorkspace } from "../components/Shell";
import { BfResourcePixelIcon } from "@bitsandflames/web-components/core";
export default function Library({
  templatesOnly = false,
}: {
  templatesOnly?: boolean;
}) {
  const {
    data: records,
    error,
    loading,
    reload,
  } = useResource<DefinitionRecord[]>("/questionnaires");
  const { data: assignments } = useResource<Assignment[]>("/assignments");
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [grid, setGrid] = useState(false);
  const [create, setCreate] = useState(false);
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState("");
  const [archive, setArchive] = useState<DefinitionRecord>();
  const file = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useWorkspace();
  const canAuthor = user.roles.some((r) =>
    ["iron:admin", "iron:author"].includes(r),
  );
  async function createForm(templateId?: string) {
    setBusy(true);
    setFormError("");
    try {
      const t = templates.find((t) => t.id === templateId);
      const d = await write<DefinitionRecord>(
        "/questionnaires",
        t
          ? { questionnaire: t.questionnaire, category: t.category }
          : { title: title.trim() || "Untitled questionnaire" },
      );
      navigate(`/questionnaires/${d.id}`);
    } catch (e) {
      setFormError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  async function importFile(selected?: File) {
    if (!selected) return;
    if (selected.size > 1_000_000) {
      toast("Import files must be smaller than 1 MB.", true);
      return;
    }
    setBusy(true);
    try {
      const questionnaire = JSON.parse(await selected.text());
      const d = await write<DefinitionRecord>("/questionnaires", {
        questionnaire,
        category: "Imported",
      });
      toast("Questionnaire imported. Review compatibility before publishing.");
      navigate(`/questionnaires/${d.id}`);
    } catch (e) {
      toast((e as Error).message, true);
    } finally {
      setBusy(false);
      if (file.current) file.current.value = "";
    }
  }
  const active = (records || []).filter((r) => !r.archived);
  const filtered = (records || []).filter(
    (r) =>
      (filter === "archived" ? r.archived : !r.archived) &&
      (filter !== "published" || r.published.length > 0) &&
      (filter !== "draft" || !r.published.length) &&
      `${r.title} ${r.category} ${r.description}`
        .toLowerCase()
        .includes(search.toLowerCase()),
  );
  if (loading) return <Spinner label="Loading questionnaires…" />;
  if (error)
    return (
      <div className="page">
        <ErrorBox message={error} retry={reload} />
      </div>
    );
  const templateCards = (
    <div className="template-grid">
      {templates.map((t, i) => (
        <button
          key={t.id}
          className={`template-card template-${t.color}`}
          onClick={() => createForm(t.id)}
          disabled={busy || !canAuthor}
        >
          <div className="template-art">
            <span className="template-art-index">0{i + 1}</span>
            <img
              className="template-pixel-art"
              src={`/illustrations/${["wellbeing", "visit", "feedback"][i]}-pixel.svg`}
              alt=""
              draggable={false}
            />
          </div>
          <div className="template-copy">
            <small>{t.category}</small>
            <h3>
              {t.title}
              <ArrowUpRight size={17} />
            </h3>
            <p>{t.subtitle}</p>
            <span>
              {
                flatten(t.questionnaire.item).filter(
                  (i) => !["group", "display"].includes(i.type),
                ).length
              }{" "}
              questions <span>·</span> Customizable
            </span>
          </div>
        </button>
      ))}
    </div>
  );
  return (
    <div className="page library-page">
      <div className="breadcrumb">
        Workspace <ChevronRight size={12} />{" "}
        {templatesOnly ? "Template library" : "Questionnaires"}
      </div>
      <div className="page-heading">
        <div>
          <div className="eyebrow">
            {templatesOnly
              ? "A THOUGHTFUL HEAD START"
              : "YOUR QUESTIONNAIRE WORKSPACE"}
          </div>
          <h1>
            {templatesOnly ? "Start with a good question" : "Questionnaires"}
            <span className="heading-dot">.</span>
          </h1>
          <p>
            {templatesOnly
              ? "Make these starting points your own. Review every form before clinical use."
              : "Build thoughtful forms. Capture meaningful answers."}
          </p>
        </div>
        <div className="heading-actions">
          {canAuthor && (
            <>
              <Button
                variant="secondary"
                onClick={() => file.current?.click()}
                disabled={busy}
              >
                <Upload size={16} />
                Import FHIR
              </Button>
              <Button
                onClick={() => {
                  setCreate(true);
                  setFormError("");
                }}
              >
                <Plus size={17} />
                New questionnaire
              </Button>
            </>
          )}
          <input
            ref={file}
            type="file"
            accept=".json,application/json"
            hidden
            onChange={(e) => importFile(e.target.files?.[0])}
          />
        </div>
      </div>
      {templatesOnly ? (
        <>
          {formError && <ErrorBox message={formError} />}
          <div className="template-intro">
            <Layers3 size={22} />
            <p>
              Reusable foundations for patient conversations. These are example
              forms, not validated clinical instruments.
            </p>
          </div>
          {templateCards}
        </>
      ) : (
        <>
          <div className="welcome-banner">
            <div className="welcome-text">
              <Badge tone="warm">
                <span className="tiny-dot" /> BUILT AROUND YOUR PATIENTS
              </Badge>
              <h2>A little clarity goes a long way.</h2>
              <p>
                From the first question to the follow-up conversation,
                <br className="desktop-only" /> keep your patients at the heart
                of every form.
              </p>
              <Link to="/templates">
                Find your starting point <ArrowUpRight size={16} />
              </Link>
            </div>
            <div className="banner-art" aria-hidden="true">
              <img
                className="questionnaire-pixel-art"
                src="/illustrations/questionnaire-pixel.svg"
                alt=""
                draggable={false}
              />
            </div>
          </div>
          <div className="metrics">
            <div>
              <span className="metric-icon">
                <BfResourcePixelIcon resource="Questionnaire" size={18} />
              </span>
              <div>
                <strong>{active.length}</strong>
                <span>Questionnaires</span>
              </div>
            </div>
            <div>
              <span className="metric-icon metric-green">
                <ClipboardCheck size={18} />
              </span>
              <div>
                <strong>
                  {active.filter((r) => r.published.length).length}
                </strong>
                <span>Published & ready</span>
              </div>
            </div>
            <div>
              <span className="metric-icon metric-orange">
                <Clock3 size={18} />
              </span>
              <div>
                <strong>
                  {
                    (assignments || []).filter(
                      (a) =>
                        !["completed", "cancelled"].includes(a.status) &&
                        Date.parse(a.expiresAt) > Date.now(),
                    ).length
                  }
                </strong>
                <span>Awaiting responses</span>
              </div>
            </div>
            <div>
              <span className="metric-icon metric-violet">
                <Check size={18} />
              </span>
              <div>
                <strong>
                  {
                    (assignments || []).filter((a) => a.status === "completed")
                      .length
                  }
                </strong>
                <span>Completed surveys</span>
              </div>
            </div>
          </div>
          <section className="library-section">
            <div className="section-heading">
              <h2>
                Your questionnaires <span>{filtered.length}</span>
              </h2>
              <div className="view-toggle">
                <button
                  aria-label="List view"
                  className={!grid ? "active" : ""}
                  onClick={() => setGrid(false)}
                >
                  <List size={17} />
                </button>
                <button
                  aria-label="Grid view"
                  className={grid ? "active" : ""}
                  onClick={() => setGrid(true)}
                >
                  <LayoutGrid size={16} />
                </button>
              </div>
            </div>
            <div className="library-toolbar">
              <div className="filter-tabs">
                {[
                  ["all", "All forms"],
                  ["published", "Published"],
                  ["draft", "Drafts"],
                  ["archived", "Archived"],
                ].map(([value, label]) => (
                  <button
                    key={value}
                    className={filter === value ? "active" : ""}
                    onClick={() => setFilter(value)}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div className="search-box">
                <Search size={16} />
                <input
                  aria-label="Search questionnaires"
                  placeholder="Search questionnaires…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>
            {filtered.length ? (
              <div
                className={grid ? "questionnaire-grid" : "questionnaire-list"}
              >
                {!grid && (
                  <div className="list-labels">
                    <span>QUESTIONNAIRE</span>
                    <span>STATUS</span>
                    <span>QUESTIONS</span>
                    <span>LAST UPDATED</span>
                    <span />
                  </div>
                )}
                {filtered.map((r, index) => (
                  <article className="questionnaire-row" key={r.id}>
                    <div className="questionnaire-name">
                      <span
                        className={`form-icon form-icon-${["violet", "orange", "green"][index % 3]}`}
                      >
                        <BfResourcePixelIcon
                          resource="Questionnaire"
                          size={21}
                        />
                      </span>
                      <div>
                        <Link to={`/questionnaires/${r.id}`}>{r.title}</Link>
                        <span>
                          {r.category}
                          {r.published.length > 0 && (
                            <>
                              <span className="dot-separator">·</span> Version{" "}
                              {r.published.at(-1)?.version}
                            </>
                          )}
                        </span>
                      </div>
                    </div>
                    <div>
                      <Badge
                        tone={
                          r.archived
                            ? "neutral"
                            : r.published.length
                              ? "green"
                              : "warm"
                        }
                      >
                        <span className="tiny-dot" />
                        {r.archived
                          ? "Archived"
                          : r.published.length
                            ? "Published"
                            : "Draft"}
                      </Badge>
                    </div>
                    <span className="question-count">
                      {
                        flatten(r.draft.item).filter(
                          (i) => !["group", "display"].includes(i.type),
                        ).length
                      }{" "}
                      <span>questions</span>
                    </span>
                    <span className="updated-date">{date(r.updatedAt)}</span>
                    <div className="row-actions">
                      {canAuthor && (
                        <>
                          <button
                            className="icon-button"
                            aria-label={`Duplicate ${r.title}`}
                            onClick={async () => {
                              try {
                                const copy = await write<DefinitionRecord>(
                                  `/questionnaires/${r.id}/duplicate`,
                                  {},
                                );
                                navigate(`/questionnaires/${copy.id}`);
                              } catch (e) {
                                toast((e as Error).message, true);
                              }
                            }}
                          >
                            <Copy size={15} />
                          </button>
                          <button
                            className="icon-button"
                            aria-label={
                              r.archived
                                ? `Restore ${r.title}`
                                : `Archive ${r.title}`
                            }
                            onClick={() => setArchive(r)}
                          >
                            <Archive size={15} />
                          </button>
                        </>
                      )}
                      <Link
                        className="icon-button row-open"
                        aria-label={`Open ${r.title}`}
                        to={`/questionnaires/${r.id}`}
                      >
                        <ArrowUpRight size={18} />
                      </Link>
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <Empty
                title={
                  search ? "No matching questionnaires" : "A fresh page awaits"
                }
                action={
                  !search &&
                  canAuthor && (
                    <Button onClick={() => setCreate(true)}>
                      <Plus size={15} />
                      Create a questionnaire
                    </Button>
                  )
                }
              >
                {search
                  ? "Try a different name or category."
                  : "Create a questionnaire or start with a template."}
              </Empty>
            )}
            <div className="list-footer">
              <span>
                <Check size={13} /> FHIR R4 questionnaires
              </span>
              <span>
                {filtered.length}{" "}
                {filtered.length === 1 ? "questionnaire" : "questionnaires"}
              </span>
            </div>
          </section>
          <section className="starter-section">
            <div className="section-heading">
              <div>
                <h2>A head start, made for you</h2>
                <p>Start with a template. Make it your own.</p>
              </div>
              <Link className="text-link" to="/templates">
                View template library <ArrowUpRight size={15} />
              </Link>
            </div>
            {templateCards}
          </section>
        </>
      )}
      <footer className="page-footer">
        <span>
          Thoughtfully built by <strong>Bits & Flames</strong>
        </span>
        <span>
          FHIR-native <span className="dot-separator">·</span> Patient-centered
        </span>
      </footer>
      <Modal
        open={create}
        onClose={() => !busy && setCreate(false)}
        title="A new beginning"
        subtitle="Name your questionnaire, then build it question by question."
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            createForm();
          }}
        >
          <Field
            label="Questionnaire title"
            placeholder="e.g. Your first appointment"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
            autoFocus
          />
          {formError && <ErrorBox message={formError} />}
          <div className="modal-actions">
            <Button
              variant="secondary"
              onClick={() => setCreate(false)}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              {busy ? (
                <Loader2 size={16} className="spin" />
              ) : (
                <Plus size={16} />
              )}
              Create questionnaire
            </Button>
          </div>
        </form>
      </Modal>
      <Modal
        open={!!archive}
        onClose={() => setArchive(undefined)}
        title={
          archive?.archived
            ? "Restore questionnaire?"
            : "Archive questionnaire?"
        }
        subtitle={archive?.title}
      >
        <p>
          {archive?.archived
            ? "This form will return to your library."
            : "The form will leave your active library. Existing assignments and responses will be retained."}
        </p>
        <div className="modal-actions">
          <Button variant="secondary" onClick={() => setArchive(undefined)}>
            Cancel
          </Button>
          <Button
            onClick={async () => {
              if (!archive) return;
              try {
                await write(`/questionnaires/${archive.id}/archive`, {
                  revision: archive.revision,
                  archived: !archive.archived,
                });
                setArchive(undefined);
                reload();
              } catch (e) {
                toast((e as Error).message, true);
              }
            }}
          >
            {archive?.archived ? "Restore" : "Archive"}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
